import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Archive, PackagePlus } from 'lucide-react';
import { useCurrencyField } from '@vaptmenu/money';
import {
  AI_UNIT_PACKS_URL,
  archiveAiUnitPack,
  archiveAiUnitPackUrl,
  describeError,
  describeFieldError,
  isRouteMissing,
  listAiUnitPacks,
  problemErrorCode,
  problemFieldErrors,
  ROUTE_MISSING_MESSAGE,
  saveAiUnitPack,
  type AiUnitPack,
  type AiUnitPackVersion,
  type SaveAiUnitPackPayload,
} from '@/lib/api';
import {
  formatBrl,
  formatDateTime,
  formatUnitPrice,
  packVersionSituation,
  parseWholeNumber,
  scheduledPackVersions,
  unitsLabel,
  validityLabel,
} from '@/lib/ai-units';
import { useIdempotencyKey } from '@/lib/idempotency';
import { Badge, type BadgeTone } from './ui';
import { CardTable, THead, TBody, Tr, Th, Td } from './CardTable';
import { ReinforcedConfirmDialog } from './ReinforcedConfirmDialog';

/**
 * Aba "Pacotes" de `/precos` (change billing-ai-units, decisão 27; requisito "Pacotes de
 * atendimentos na página de preços" de `platform-backoffice`). Só para Admin: quem chama decide,
 * e a API recusa os demais com 403.
 *
 * Cada código mostra a versão vigente (atendimentos, preço, preço por atendimento e validade), as
 * agendadas e se está arquivado. A versão nova vale para as compras feitas a partir da vigência
 * dela: compra já feita guarda o preço e a validade da época, e a tela diz isso antes de salvar.
 * Os erros de invariante voltam por campo (400) e aparecem junto do campo. Arquivar tira o código
 * da venda agora e cancela a versão agendada, com confirmação reforçada (o código digitado).
 */

/** O mesmo padrão do código na API (`AiUnitPack.CodePattern`). */
const PACK_CODE_PATTERN = /^[a-z0-9_]{3,40}$/;
const MIN_UNITS = 1;
const MAX_UNITS = 100_000;
const MIN_PRICE = 5;
const MIN_VALIDITY_MONTHS = 1;
const MAX_VALIDITY_MONTHS = 24;
const DEFAULT_VALIDITY_MONTHS = 12;

/** Campos do formulário que a API valida, na ordem em que aparecem. */
const PACK_FIELDS = ['code', 'units', 'price', 'validityMonths', 'effectiveFrom'] as const;

function packStatus(pack: AiUnitPack): { label: string; tone: BadgeTone } {
  if (pack.isArchived) return { label: 'Arquivado', tone: 'neutral' };
  if (pack.isForSale) return { label: 'À venda', tone: 'success' };
  return { label: 'Agendado', tone: 'info' };
}

function versionSummary(version: AiUnitPackVersion): string {
  return `${unitsLabel(version.units)} · ${formatBrl(version.price)} · ${validityLabel(version.validityMonths)}`;
}

export function AiUnitPacksPanel() {
  const [packs, setPacks] = useState<AiUnitPack[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [draftFrom, setDraftFrom] = useState<{ pack: AiUnitPack | null; nonce: number }>({ pack: null, nonce: 0 });
  const [archiving, setArchiving] = useState<AiUnitPack | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    listAiUnitPacks()
      .then((data) => {
        if (cancelled) return;
        setPacks(data);
        setError(null);
      })
      .catch((err) => !cancelled && setError(isRouteMissing(err) ? ROUTE_MISSING_MESSAGE : describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return (
    <section aria-label="Pacotes de atendimentos">
      <div className="info-banner" style={{ marginBottom: 16 }} data-testid="packs-purchase-note">
        Compras já feitas mantêm o preço e a validade da época. Uma versão nova vale só para as compras feitas a partir
        da vigência dela, e os atendimentos já comprados continuam valendo até o vencimento deles.
      </div>

      {notice && (
        <div className="info-banner" role="status" style={{ marginBottom: 16 }}>
          {notice}
        </div>
      )}
      {error && !packs && (
        <div className="error-banner" role="alert" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}
      {!packs && !error && <p className="muted">Carregando…</p>}

      {packs && (
        <>
          <PackCatalog
            packs={packs}
            onNewVersion={(pack) => setDraftFrom((prev) => ({ pack, nonce: prev.nonce + 1 }))}
            onArchive={(pack) => {
              setNotice(null);
              setArchiving(pack);
            }}
          />
          <NewPackVersionForm
            key={draftFrom.nonce}
            packs={packs}
            initial={draftFrom.pack}
            onSaved={(message) => {
              setNotice(message);
              setDraftFrom((prev) => ({ pack: null, nonce: prev.nonce + 1 }));
              reload();
            }}
          />
          <PackHistory packs={packs} />
        </>
      )}

      {archiving && (
        <ArchivePackDialog
          pack={archiving}
          onDone={(message) => {
            setArchiving(null);
            setNotice(message);
            reload();
          }}
          onCancel={() => setArchiving(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function PackCatalog({
  packs,
  onNewVersion,
  onArchive,
}: {
  packs: AiUnitPack[];
  onNewVersion: (pack: AiUnitPack) => void;
  onArchive: (pack: AiUnitPack) => void;
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Pacotes de atendimentos</h2>
      {packs.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nenhum pacote no catálogo. Sem pacote à venda, o painel não oferece a compra de atendimentos.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Pacotes de atendimentos">
            <THead>
              <Tr>
                <Th>Código</Th>
                <Th>Situação</Th>
                <Th>Vigente</Th>
                <Th>Por atendimento</Th>
                <Th>Agendadas</Th>
                <Th>Ações</Th>
              </Tr>
            </THead>
            <TBody>
              {packs.map((pack) => {
                const status = packStatus(pack);
                const scheduled = scheduledPackVersions(pack.versions, pack.current);
                return (
                  <Tr key={pack.code} data-testid={`pack-${pack.code}`}>
                    <Td heading className="mono">
                      {pack.code}
                    </Td>
                    <Td label="Situação">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td label="Vigente">
                      {pack.current ? (
                        <>
                          {versionSummary(pack.current)}
                          <div className="muted" style={{ fontSize: 12 }}>
                            desde {formatDateTime(pack.current.effectiveFrom)}
                          </div>
                        </>
                      ) : (
                        <span className="muted">Nenhuma ainda</span>
                      )}
                    </Td>
                    <Td label="Por atendimento">
                      {pack.current ? formatUnitPrice(pack.current.unitPrice) : <span className="muted">—</span>}
                    </Td>
                    <Td label="Agendadas">
                      {scheduled.length === 0 ? (
                        <span className="muted">Nenhuma</span>
                      ) : (
                        scheduled.map((v) => (
                          <div key={v.id} data-testid="pack-scheduled">
                            {versionSummary(v)}
                            <span className="muted"> · a partir de {formatDateTime(v.effectiveFrom)}</span>
                          </div>
                        ))
                      )}
                    </Td>
                    <Td action>
                      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onNewVersion(pack)}>
                          Nova versão
                        </button>
                        {!pack.isArchived && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => onArchive(pack)}
                            aria-label={`Arquivar ${pack.code}`}
                          >
                            <Archive size={14} /> Arquivar
                          </button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewPackVersionForm({
  packs,
  initial,
  onSaved,
}: {
  packs: AiUnitPack[];
  initial: AiUnitPack | null;
  onSaved: (message: string) => void;
}) {
  const base = initial?.current ?? initial?.scheduled ?? null;
  const [code, setCode] = useState(initial?.code ?? '');
  const [units, setUnits] = useState(base ? String(base.units) : '');
  const [price, setPrice] = useState<number | null>(base?.price ?? null);
  const [validity, setValidity] = useState(String(base?.validityMonths ?? DEFAULT_VALIDITY_MONTHS));
  const [when, setWhen] = useState<'now' | 'scheduled'>('now');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const priceField = useCurrencyField({ value: price, onValueChange: setPrice });

  const trimmedCode = code.trim();
  const parsedUnits = parseWholeNumber(units);
  const parsedValidity = parseWholeNumber(validity);
  const existing = packs.find((p) => p.code === trimmedCode) ?? null;

  const localError = useMemo(() => {
    if (trimmedCode && !PACK_CODE_PATTERN.test(trimmedCode)) {
      return 'O código deve ter de 3 a 40 caracteres, só letras minúsculas, dígitos e sublinhado.';
    }
    if (units.trim() && (parsedUnits === null || parsedUnits < MIN_UNITS || parsedUnits > MAX_UNITS)) {
      return 'A quantidade de atendimentos deve ser um número inteiro de 1 a 100.000.';
    }
    if (price !== null && price < MIN_PRICE) {
      return 'O preço deve ser de no mínimo R$ 5,00, o menor valor de cobrança aceito pelo Asaas.';
    }
    if (
      validity.trim() &&
      (parsedValidity === null || parsedValidity < MIN_VALIDITY_MONTHS || parsedValidity > MAX_VALIDITY_MONTHS)
    ) {
      return 'A validade deve ser de 1 a 24 meses.';
    }
    if (when === 'scheduled' && !effectiveFrom) return 'Informe o início da vigência.';
    return null;
  }, [trimmedCode, units, parsedUnits, price, validity, parsedValidity, when, effectiveFrom]);

  const complete = trimmedCode !== '' && parsedUnits !== null && price !== null && parsedValidity !== null;
  const unitPricePreview =
    complete && !localError && parsedUnits ? formatUnitPrice(Math.round((price / parsedUnits) * 10_000) / 10_000) : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!complete || localError || submitting || parsedUnits === null || parsedValidity === null || price === null) {
      return;
    }

    const effectiveIso = when === 'scheduled' ? new Date(effectiveFrom).toISOString() : null;
    const whenText = effectiveIso ? `a partir de ${formatDateTime(effectiveIso)}` : 'a partir de agora';
    const message =
      `Pacote ${trimmedCode}: ${unitsLabel(parsedUnits)} por ${formatBrl(price)}, válidos por ` +
      `${validityLabel(parsedValidity)} a partir do pagamento, ${whenText}.\n\n` +
      'Compras já feitas mantêm o preço e a validade da época.' +
      (existing?.isArchived ? '\n\nO código está arquivado e volta à venda quando esta versão valer.' : '') +
      '\n\nConfirmar?';
    if (!confirm(message)) return;

    const payload: SaveAiUnitPackPayload = {
      code: trimmedCode,
      units: parsedUnits,
      price,
      validityMonths: parsedValidity,
      effectiveFrom: effectiveIso,
    };
    const key = idempotency.keyFor(AI_UNIT_PACKS_URL, payload);
    setSubmitting(true);
    setFieldErrors({});
    setError(null);
    try {
      const saved = await saveAiUnitPack(payload, { idempotencyKey: key });
      idempotency.settle();
      const effectiveText =
        Date.parse(saved.version.effectiveFrom) > Date.now()
          ? `agendada para ${formatDateTime(saved.version.effectiveFrom)}`
          : 'valendo desde já';
      onSaved(
        `Versão de ${saved.version.code} salva, ${effectiveText}.` +
          (saved.reactivated ? ' O código volta à venda quando ela valer.' : ''),
      );
    } catch (err) {
      idempotency.settle(err);
      const byField = problemFieldErrors(err);
      if (Object.keys(byField).length > 0) {
        setFieldErrors(byField);
        const unknown = Object.keys(byField).filter((f) => !(PACK_FIELDS as readonly string[]).includes(f));
        if (unknown.length > 0) setError(describeFieldError(err));
      } else {
        setError(describeError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldError = (field: (typeof PACK_FIELDS)[number]) =>
    fieldErrors[field]?.length ? (
      <div className="error-banner" role="alert" style={{ marginTop: 6, padding: '8px 12px' }} data-testid={`pack-error-${field}`}>
        {fieldErrors[field][0]}
      </div>
    ) : null;

  return (
    <form className="card" style={{ marginBottom: 16 }} onSubmit={submit} aria-label="Nova versão de pacote">
      <h2 style={{ marginTop: 0, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
        <PackagePlus size={16} /> Nova versão de pacote
      </h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Use um código existente para mudar o preço, a quantidade ou a validade dele, ou um código novo para criar um
        pacote. A validade conta a partir do pagamento de cada compra.
      </p>
      <div className="grid-2">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pack-code">Código</label>
          <input
            id="pack-code"
            list="pack-codes"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="units_300"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={fieldErrors.code ? true : undefined}
          />
          <datalist id="pack-codes">
            {packs.map((p) => (
              <option key={p.code} value={p.code} />
            ))}
          </datalist>
          {fieldError('code')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pack-units">Atendimentos</label>
          <input
            id="pack-units"
            type="number"
            inputMode="numeric"
            min={MIN_UNITS}
            max={MAX_UNITS}
            step={1}
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            aria-invalid={fieldErrors.units ? true : undefined}
          />
          {fieldError('units')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pack-price">Preço (R$)</label>
          <input id="pack-price" placeholder="99,90" aria-invalid={fieldErrors.price ? true : undefined} {...priceField} />
          {fieldError('price')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pack-validity">Validade (meses)</label>
          <input
            id="pack-validity"
            type="number"
            inputMode="numeric"
            min={MIN_VALIDITY_MONTHS}
            max={MAX_VALIDITY_MONTHS}
            step={1}
            value={validity}
            onChange={(e) => setValidity(e.target.value)}
            aria-invalid={fieldErrors.validityMonths ? true : undefined}
          />
          {fieldError('validityMonths')}
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="pack-when">Vigência</label>
          <select id="pack-when" value={when} onChange={(e) => setWhen(e.target.value as 'now' | 'scheduled')}>
            <option value="now">Agora</option>
            <option value="scheduled">Agendar</option>
          </select>
        </div>
        {when === 'scheduled' && (
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="pack-effective">A partir de</label>
            <input
              id="pack-effective"
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              aria-invalid={fieldErrors.effectiveFrom ? true : undefined}
            />
            {fieldError('effectiveFrom')}
          </div>
        )}
      </div>
      {when === 'now' && fieldError('effectiveFrom')}

      {unitPricePreview && (
        <p className="muted" style={{ marginBottom: 0 }} data-testid="pack-preview">
          {unitPricePreview} por atendimento
          {existing?.isArchived && ' · o código está arquivado e volta à venda quando esta versão valer'}
        </p>
      )}
      {localError && (
        <p className="muted" role="status">
          {localError}
        </p>
      )}
      {error && (
        <div className="error-banner" role="alert" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="submit" className="btn" disabled={!complete || !!localError || submitting}>
          {submitting ? 'Salvando…' : 'Salvar versão'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function PackHistory({ packs }: { packs: AiUnitPack[] }) {
  const rows = packs.flatMap((pack) => pack.versions.map((version) => ({ pack, version })));
  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 15 }}>Histórico de versões</h2>
      {rows.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nenhuma versão registrada.
        </p>
      ) : (
        <div className="table-scroll">
          <CardTable aria-label="Histórico de versões dos pacotes">
            <THead>
              <Tr>
                <Th>Código</Th>
                <Th>Vigência</Th>
                <Th>Versão</Th>
                <Th>Por atendimento</Th>
                <Th>Situação</Th>
                <Th>Criada em</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map(({ pack, version }) => {
                const situation = packVersionSituation(version, pack.current);
                return (
                  <Tr key={version.id}>
                    <Td heading className="mono">
                      {version.code}
                    </Td>
                    <Td label="Vigência">{formatDateTime(version.effectiveFrom)}</Td>
                    <Td label="Versão">{versionSummary(version)}</Td>
                    <Td label="Por atendimento">{formatUnitPrice(version.unitPrice)}</Td>
                    <Td label="Situação">
                      <Badge tone={situation.tone}>{situation.label}</Badge>
                      {version.archivedAt && !version.cancelledByArchive && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          arquivada em {formatDateTime(version.archivedAt)}
                        </div>
                      )}
                    </Td>
                    <Td label="Criada em" className="muted">
                      {formatDateTime(version.createdAt)}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </CardTable>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ArchivePackDialog({
  pack,
  onDone,
  onCancel,
}: {
  pack: AiUnitPack;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();
  const scheduled = scheduledPackVersions(pack.versions, pack.current);

  async function archive() {
    const key = idempotency.keyFor(archiveAiUnitPackUrl(pack.code), {});
    setBusy(true);
    setError(null);
    try {
      await archiveAiUnitPack(pack.code, { idempotencyKey: key });
      idempotency.settle();
      onDone(`Pacote ${pack.code} arquivado: saiu da venda, e as compras já feitas continuam valendo.`);
    } catch (err) {
      idempotency.settle(err);
      setError(
        problemErrorCode(err) === 'billing.ai_units_pack_not_found'
          ? 'Este código não está mais no catálogo. Recarregue a página.'
          : describeError(err),
      );
      setBusy(false);
    }
  }

  return (
    <ReinforcedConfirmDialog
      title={`Arquivar o pacote ${pack.code}`}
      description={
        <>
          <p style={{ margin: '0 0 8px' }}>
            O pacote sai da venda agora: o painel deixa de oferecê-lo a todas as lojas.
            {scheduled.length > 0 && ' As versões agendadas dele são canceladas e não passam a valer.'}
          </p>
          <p style={{ margin: 0 }}>
            Compras já feitas mantêm o preço e a validade da época, e os atendimentos já comprados continuam valendo.
            Para voltar a vender, crie uma versão nova com o mesmo código.
          </p>
        </>
      }
      acknowledgement={
        <>
          Entendo que <strong>{pack.code}</strong> deixa de ser vendido agora.
        </>
      }
      confirmPhrase={pack.code}
      confirmLabel="Arquivar"
      busy={busy}
      error={error}
      onConfirm={archive}
      onCancel={onCancel}
    />
  );
}
